import { KeyRound, LogOut, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { isNavItemActive } from '@/lib/admin-nav';
import { EMPLOYEE_NAV } from '@/lib/employee-nav';
import { signOut, useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

/**
 * The employee portal's sidebar — ROADMAP.md §6.1.
 *
 * Deliberately the client sidebar's twin: the user card and sign-out at the
 * bottom rather than behind an avatar menu, because a cleaner opening this on a
 * phone in someone's driveway should not have to discover where anything is.
 *
 * What it does **not** carry is the "Back to the site" link. That exists on the
 * client portal because a homeowner arrives from the marketing site and wants a
 * way back; this is a work tool and the marketing site is not where anybody is
 * going next.
 *
 * Every row is `min-h-touch` — 44px — which is the phase's running theme. The
 * prototype's employee nav is the one place a finger has to hit a target one
 * handed, and it is undersized at every point measured (P23, P24).
 */
export function EmployeeSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();

  const user = session?.user;
  const initial = user?.name.trim().charAt(0).toUpperCase() ?? '?';

  async function onSignOut(): Promise<void> {
    await signOut();
    await navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full flex-col">
      <nav aria-label="Portal sections" className="flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-4 flex items-center justify-between px-2">
          <span className="text-h3 text-bark-700">Honeycomb</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close menu"
            className="lg:hidden"
            onClick={onNavigate}
          >
            <X aria-hidden="true" />
          </Button>
        </div>

        <ul className="space-y-0.5">
          {EMPLOYEE_NAV.map((item) => {
            const active = isNavItemActive(item.to, pathname);
            const Icon = item.icon;

            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label',
                    active
                      ? // ink-900 on honey-400 is 14.75:1. Never honey as text.
                        'bg-honey-400 text-ink-900'
                      : 'text-ink-700 hover:bg-honey-50',
                  )}
                >
                  <Icon aria-hidden="true" className="size-[18px] shrink-0" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t border-ink-300 p-3">
        <div className="flex items-center gap-2.5 px-1 py-1.5">
          {/* ink-900 on honey-400 at 14.75:1. The prototype's avatar initial is
              honey on honey — 1.0:1, so the letter is simply not there (P27). */}
          <span
            aria-hidden="true"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-honey-400 text-label font-semibold text-ink-900"
          >
            {initial}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-label leading-tight text-ink-900">
              {user?.name}
            </span>
            {/* Sam signs in with a username and has no email at all — the P9
                shape. Saying so beats an empty line or a dash. */}
            <span className="block truncate text-meta leading-tight text-ink-500">
              {user?.email ?? <span className="italic">Signs in with a username</span>}
            </span>
          </span>
        </div>

        <Link
          to="/change-password"
          onClick={onNavigate}
          className="mt-1 flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label text-ink-700 hover:bg-honey-50"
        >
          <KeyRound aria-hidden="true" className="size-[18px] shrink-0" />
          Change password
        </Link>

        <button
          type="button"
          onClick={() => void onSignOut()}
          className="flex min-h-touch w-full items-center gap-2.5 rounded-md px-2 text-label text-ink-700 hover:bg-honey-50"
        >
          <LogOut aria-hidden="true" className="size-[18px] shrink-0" />
          Sign out
        </button>
      </div>
    </div>
  );
}
